/**
 * Attendance — staff page.
 *
 * Two modes via tabs:
 *  1. "Mark today" — pick a class+date, batch-toggle each student's status
 *     (present | absent | leave | late) and submit.
 *  2. "Summary"     — date-range attendance % per student in scope.
 *  3. "Leave inbox" — pending leave applications from parents; decide.
 */
import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const STATUSES = ["present", "absent", "leave", "late"];
const today = () => new Date().toISOString().slice(0, 10);

function MarkTab() {
  const [students, setStudents] = useState([]);
  const [classFilter, setClassFilter] = useState("");
  const [date, setDate] = useState(today());
  const [marks, setMarks] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/students").then((r) => setStudents(Array.isArray(r.data) ? r.data : []));
  }, []);

  const classOptions = useMemo(() => {
    const s = new Set();
    students.forEach((st) => { if (st.class_name) s.add(st.class_name); });
    return Array.from(s).sort();
  }, [students]);

  const filtered = useMemo(
    () => students.filter((s) => !classFilter || s.class_name === classFilter),
    [students, classFilter],
  );

  useEffect(() => {
    // Default all visible students to "present"
    const m = {};
    filtered.forEach((s) => { m[s.id] = marks[s.id] || "present"; });
    setMarks(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered.length, classFilter]);

  const submit = async () => {
    setBusy(true);
    try {
      const rows = filtered.map((s) => ({ student_id: s.id, status: marks[s.id] || "present" }));
      await api.post("/attendance/mark", { date, class_name: classFilter || null, rows });
      toast.success(`Marked ${rows.length} students for ${date}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex gap-3 items-end flex-wrap">
        <div className="flex-1 min-w-[200px]">
          <Label>Class</Label>
          <Select value={classFilter || "all"} onValueChange={(v) => setClassFilter(v === "all" ? "" : v)}>
            <SelectTrigger data-testid="att-class" className="bg-white"><SelectValue placeholder="All classes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All classes</SelectItem>
              {classOptions.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Date</Label>
          <Input data-testid="att-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-white" />
        </div>
        <Button data-testid="att-mark-submit" onClick={submit} disabled={busy || filtered.length === 0}>
          {busy ? "Saving…" : `Mark ${filtered.length}`}
        </Button>
      </div>
      <div className="vs-card divide-y">
        {filtered.length === 0 && (
          <div className="p-6 text-center text-sm text-[var(--vs-text-secondary)]" data-testid="att-empty">
            No students in scope. Try clearing the class filter.
          </div>
        )}
        {filtered.map((s) => (
          <div key={s.id} className="flex items-center gap-3 p-3" data-testid={`att-row-${s.id}`}>
            <div className="flex-1">
              <div className="font-medium text-sm">{s.name}</div>
              <div className="text-xs text-[var(--vs-text-secondary)] font-mono">{s.vs_id} · {s.class_name || "—"}</div>
            </div>
            <div className="flex gap-1">
              {STATUSES.map((st) => (
                <button
                  key={st}
                  type="button"
                  data-testid={`att-${s.id}-${st}`}
                  onClick={() => setMarks((m) => ({ ...m, [s.id]: st }))}
                  className={`capitalize text-xs px-2 py-1 rounded border transition ${
                    marks[s.id] === st ? "bg-[var(--vs-primary)] text-white border-[var(--vs-primary)]" : "bg-white border-[var(--vs-border)]"
                  }`}
                >
                  {st}
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SummaryTab() {
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [to, setTo] = useState(today());
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/attendance/summary", { params: { date_from: from, date_to: to } });
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  return (
    <div className="space-y-3">
      <div className="flex gap-3 items-end flex-wrap">
        <div><Label>From</Label><Input data-testid="sum-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="bg-white" /></div>
        <div><Label>To</Label><Input data-testid="sum-to" type="date" value={to} onChange={(e) => setTo(e.target.value)} className="bg-white" /></div>
        <Button data-testid="sum-load" onClick={load} disabled={loading}>{loading ? "…" : "Load"}</Button>
      </div>
      <div className="vs-card divide-y">
        {rows.map((r) => (
          <div key={r.student_id} className="flex items-center gap-3 p-3" data-testid={`sum-row-${r.student_id}`}>
            <div className="flex-1">
              <div className="font-medium text-sm">{r.name}</div>
              <div className="text-xs text-[var(--vs-text-secondary)] font-mono">{r.vs_id} · {r.class_name || "—"}</div>
            </div>
            <div className="text-sm flex gap-3 font-mono">
              <span className="text-emerald-600">P:{r.present}</span>
              <span className="text-red-600">A:{r.absent}</span>
              <span className="text-amber-600">L:{r.leave}</span>
              <span className="font-bold w-12 text-right">{r.percentage != null ? `${r.percentage}%` : "—"}</span>
            </div>
          </div>
        ))}
        {!rows.length && <div className="p-4 text-center text-sm text-[var(--vs-text-secondary)]">No data for this range yet.</div>}
      </div>
    </div>
  );
}

function LeaveInboxTab() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const [busy, setBusy] = useState({});

  const load = async () => {
    const r = await api.get("/attendance/leave", { params: { status } });
    setRows(r.data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status]);

  const decide = async (id, decision) => {
    setBusy((b) => ({ ...b, [id]: decision }));
    try {
      await api.post(`/attendance/leave/${id}/decide`, { decision });
      toast.success(`Leave ${decision}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy((b) => ({ ...b, [id]: null }));
    }
  };

  return (
    <div className="space-y-3">
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger data-testid="leave-status-filter" className="bg-white w-48"><SelectValue /></SelectTrigger>
        <SelectContent>
          {["pending", "approved", "rejected"].map((s) => (<SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>))}
        </SelectContent>
      </Select>
      <div className="vs-card divide-y">
        {rows.map((r) => (
          <div key={r.id} className="p-3 space-y-2" data-testid={`leave-row-${r.id}`}>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium text-sm">{r.student_id}</div>
                <div className="text-xs text-[var(--vs-text-secondary)]">{r.from_date} → {r.to_date}</div>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded capitalize ${r.status === "pending" ? "bg-amber-100 text-amber-700" : r.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{r.status}</span>
            </div>
            <div className="text-sm">{r.reason}</div>
            {r.status === "pending" && (
              <div className="flex gap-2">
                <Button size="sm" data-testid={`leave-approve-${r.id}`} onClick={() => decide(r.id, "approved")} disabled={!!busy[r.id]}>Approve</Button>
                <Button size="sm" variant="outline" data-testid={`leave-reject-${r.id}`} onClick={() => decide(r.id, "rejected")} disabled={!!busy[r.id]}>Reject</Button>
              </div>
            )}
          </div>
        ))}
        {!rows.length && <div className="p-4 text-center text-sm text-[var(--vs-text-secondary)]">No {status} leave applications.</div>}
      </div>
    </div>
  );
}

export default function Attendance() {
  return (
    <DashboardLayout>
      <div className="vs-section">
        <h1 className="text-2xl font-semibold">Attendance</h1>
        <p className="text-sm text-[var(--vs-text-secondary)]">Daily attendance, summary stats, and parent leave applications.</p>
      </div>
      <Tabs defaultValue="mark">
        <TabsList>
          <TabsTrigger value="mark" data-testid="tab-mark">Mark today</TabsTrigger>
          <TabsTrigger value="summary" data-testid="tab-summary">Summary</TabsTrigger>
          <TabsTrigger value="leave" data-testid="tab-leave">Leave inbox</TabsTrigger>
        </TabsList>
        <TabsContent value="mark"><MarkTab /></TabsContent>
        <TabsContent value="summary"><SummaryTab /></TabsContent>
        <TabsContent value="leave"><LeaveInboxTab /></TabsContent>
      </Tabs>
    </DashboardLayout>
  );
}
